import { forwardRef, useState, useRef, useEffect, ChangeEvent, KeyboardEvent, FocusEvent } from 'react'
import { cva } from 'class-variance-authority'
import { cn } from '@renderer/chat'

interface InputFieldProps {
  label?: string
  autoFocus?: boolean
  className?: string
  defaultValue?: string
  value?: string
  disabled?: boolean
  debounce?: number
  id?: string
  placeholder?: string
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void
  onFocus?: (event: FocusEvent<HTMLInputElement>) => void
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void
  onClick?: (event: React.MouseEvent<HTMLInputElement>) => void
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void
  onSubmit?: (value: string) => void
  size: 'small' | 'medium' | 'large' | 'xlarge' | 'xxlarge'
  style?: React.CSSProperties
  type?: string
  error?: string
  readOnly?: boolean
}

// Input styles using cva with extended size variants and a labelVisible modifier for xxlarge.
const inputFieldVariants = cva(
  'text-foreground bg-background/50 border rounded-lg font-[350] placeholder:text-foreground-placeholder hover:border-hover focus-visible:border-strong outline-none focus:bg-element',
  {
    variants: {
      size: {
        small: 'p-3 text-[12px] h-[32px]',
        medium: 'p-3 text-sm h-[36px]',
        large: 'p-4 text-sm h-[42px]',
        xlarge: 'p-4 text-[16px] h-[48px]',
        xxlarge: 'p-5 text-[16px] h-[64px] transition-all duration-200 ease-in-out',
      },
      labelVisible: {
        true: 'pt-[40px]',
        false: '',
      },
      disabled: {
        true: 'opacity-50 cursor-not-allowed',
        false: '',
      },
    },
    compoundVariants: [
      {
        size: 'xxlarge',
        labelVisible: true,
        className: 'pt-[40px]',
      },
    ],
    defaultVariants: {
      size: 'medium',
      labelVisible: false,
      disabled: false,
    },
  },
)

// Label styles using cva. For inline labels (used with xxlarge size),
// the label is positioned absolutely over the input and fades in/out.
const labelVariants = cva('text-md font-normal text-foreground', {
  variants: {
    inline: {
      true: 'absolute w-full h-full top-3 left-5 text-xs text-foreground-placeholder pointer-events-none opacity-0 transition-opacity duration-200 ease-in-out',
      false: 'mb-1',
    },
    labelVisible: {
      true: 'opacity-100',
      false: '',
    },
  },
  compoundVariants: [
    {
      inline: true,
      labelVisible: true,
      className: 'opacity-100',
    },
  ],
  defaultVariants: {
    inline: false,
    labelVisible: false,
  },
})

const errorClasses = 'text-[#FF4D4D] text-[12px] font-[350] mt-1'

const InputField = forwardRef<HTMLInputElement, InputFieldProps>(
  (
    {
      label,
      autoFocus,
      className,
      defaultValue,
      value,
      type,
      size = 'large',
      placeholder,
      onChange,
      onKeyDown,
      onClick,
      onBlur,
      onFocus,
      onSubmit,
      disabled = false,
      debounce,
      style,
      error,
      readOnly,
      id,
      ...props
    },
    ref,
  ) => {
    const [internalValue, setInternalValue] = useState<string>(defaultValue ?? '')
    const [showInlineLabel, setShowInlineLabel] = useState<boolean>(Boolean(defaultValue))
    const debounceTimeoutRef = useRef<NodeJS.Timeout | number | null>(null)
    const isControlled = value !== undefined
    const inlineLabel = size === 'xxlarge'

    // Clear debounce timer on unmount
    useEffect(() => {
      return () => {
        if (debounceTimeoutRef.current) {
          clearTimeout(debounceTimeoutRef.current as number)
        }
      }
    }, [])

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      if (isControlled) {
        onChange && onChange(e)
      } else {
        setInternalValue(e.target.value)
        if (debounce && !isNaN(debounce)) {
          if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current as number)
          }
          debounceTimeoutRef.current = setTimeout(() => {
            onChange && onChange(e)
          }, debounce)
        } else {
          onChange && onChange(e)
        }
      }
    }

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
      onKeyDown && onKeyDown(e)
      if (e.key === 'Enter' && onSubmit) {
        onSubmit(isControlled ? value! : internalValue)
      }
    }

    const handleFocus = (e: FocusEvent<HTMLInputElement>) => {
      onFocus && onFocus(e)
      if (inlineLabel) {
        setShowInlineLabel(true)
      }
    }

    const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
      onBlur && onBlur(e)
      if (inlineLabel) {
        const currentValue = isControlled ? value : internalValue
        setShowInlineLabel(Boolean(currentValue && currentValue.length))
      }
    }

    // Determine effective label visibility for inline labels.
    const effectiveLabelVisible =
      inlineLabel && (showInlineLabel || Boolean(isControlled ? value?.length : internalValue.length))

    return (
      <div className={cn('relative flex flex-col gap-2', className)}>
        {label && (
          <span
            className={cn(
              labelVariants({
                inline: inlineLabel,
                labelVisible: effectiveLabelVisible,
              }),
            )}
          >
            {label}
          </span>
        )}
        <input
          ref={ref}
          id={id}
          autoFocus={autoFocus}
          type={type}
          placeholder={placeholder}
          style={style}
          value={isControlled ? value : internalValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onClick={onClick}
          onFocus={handleFocus}
          onBlur={handleBlur}
          disabled={disabled}
          readOnly={readOnly}
          className={cn(
            inputFieldVariants({
              size,
              labelVisible: inlineLabel ? effectiveLabelVisible : false,
              disabled,
            }),
            className,
          )}
          {...props}
        />
        {error && <span className={cn(errorClasses)}>{error}</span>}
      </div>
    )
  },
)

InputField.displayName = 'InputField'

export default InputField
